// client/src/hooks/useApiClient.js
import { useCallback, useMemo } from 'react';

const forceLogout = () => {
    localStorage.removeItem('jwtToken');
    window.location.href = '/portal';
};

export const useApiClient = () => {
    const token = localStorage.getItem('jwtToken');

    const request = useCallback(async (endpoint, options = {}, isMultipart = false, responseType = null) => {
        const defaultHeaders = {
            'Authorization': `Bearer ${token}`,
        };

        // Do NOT set Content-Type for multipart/form-data; the browser does it.
        if (!isMultipart) {
            defaultHeaders['Content-Type'] = 'application/json';
        }

        const config = { ...options, headers: { ...defaultHeaders, ...options.headers } };

        const response = await fetch(endpoint, config);

        // 401 means the token is gone or expired — log out. 403 does NOT: it means
        // the caller is authenticated and simply may not do this, which is a normal
        // and frequent answer once row-level authority exists. Treating the two the
        // same ejected a property manager from the app for opening a screen outside
        // their grant, and hid every deliberate refusal the server writes.
        if (response.status === 401) {
            forceLogout();
            throw new Error('Your session has expired. Please log in again.');
        }

        if (!response.ok) {
            let errorData = {};
            try { errorData = await response.json(); } catch { /* non-JSON body */ }
            const err = new Error(errorData.message || `HTTP error! status: ${response.status}`);
            // Carry the payload. This module answers a refusal with problems[] and
            // requires[] naming exactly what to change; throwing only the summary
            // line discards the useful half.
            err.status = response.status;
            err.payload = errorData;
            err.problems = errorData.problems;
            err.requires = errorData.requires;
            throw err;
        }

        if (responseType === 'blob') {
            return { data: await response.blob() };
        }

        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
            return response.json();
        }
        return null;

    }, [token]);

    // useMemo ensures that the same object is returned on every render, preventing loops.
    const apiClient = useMemo(() => ({
        get: (endpoint) => request(endpoint, { method: 'GET' }),
        post: (endpoint, body, opts = false) => {
            const isMultipart = opts === true || opts?.isMultipart === true;
            const responseType = (opts && typeof opts === 'object') ? opts.responseType : null;
            const options = {
                method: 'POST',
                body: isMultipart ? body : JSON.stringify(body),
            };
            return request(endpoint, options, isMultipart, responseType);
        },
        put: (endpoint, body) => request(endpoint, { method: 'PUT', body: JSON.stringify(body) }),
        patch: (endpoint, body) => request(endpoint, { method: 'PATCH', body: JSON.stringify(body) }),
        // DELETE carries a body when there is one. Some refusals are deliberate and
        // demand a reason — lifting a do-not-contact suppression is the managing
        // broker's decision and the server refuses it without one (422). Dropping
        // the body silently made that endpoint impossible to satisfy from the app.
        delete: (endpoint, body) => request(endpoint, {
            method: 'DELETE',
            ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        }),
    }), [request]);

    return apiClient;
};